import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { useMarkSettledExternally } from "../api/clusters";
import type { DocumentationCluster } from "../types";

interface Props {
  clusters: DocumentationCluster[];
  selectedClusterIds: string[];
  onClose: () => void;
  onSettled: () => void;
}

export function MarkSettledDialog({ clusters, selectedClusterIds, onClose, onSettled }: Props) {
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const selectedClusters = clusters.filter((c) => selectedClusterIds.includes(c.id));
  const alreadySettled = selectedClusters.filter((c) => c.is_settled);

  const settleMutation = useMarkSettledExternally();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (selectedClusterIds.length === 0) {
      setError("Zaznacz przynajmniej jeden klaster.");
      return;
    }
    if (alreadySettled.length > 0) {
      setError("Niektóre z zaznaczonych klastrów są już rozliczone. Odznacz je i spróbuj ponownie.");
      return;
    }
    try {
      await settleMutation.mutateAsync({
        cluster_ids: selectedClusterIds,
        note: note.trim(),
      });
      onSettled();
      onClose();
    } catch (err) {
      const apiErr = (err as { response?: { data?: { errors?: unknown } } })
        ?.response?.data?.errors;
      setError(
        typeof apiErr === "string"
          ? apiErr
          : "Nie udało się oznaczyć klastrów jako rozliczone. Spróbuj ponownie.",
      );
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div role="dialog" className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg p-6">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Oznacz jako rozliczone w ramach innego zamówienia
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Wybrane klastry zostaną oznaczone jako rozliczone, bez tworzenia nowego
          zamówienia. Użyj tej opcji, gdy dokumentacja została już rozliczona w
          ramach innego (zewnętrznego) zamówienia.
        </p>

        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm">
          <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">
            Wybrane klastry ({selectedClusterIds.length}):
          </p>
          <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
            {selectedClusters.map((c) => (
              <li key={c.id}>
                • {c.name}
                {c.contractor_name ? ` (${c.contractor_name})` : ""}
                {c.is_settled && (
                  <span className="ml-1 text-amber-600 dark:text-amber-400">— już rozliczony</span>
                )}
              </li>
            ))}
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="settlement-note"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Numer / opis zamówienia (opcjonalnie)
            </label>
            <textarea
              id="settlement-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="np. ELEKTIM/2026/123 albo zamówienie zewnętrzne"
              className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Anuluj</Button>
            <Button type="submit" disabled={settleMutation.isPending}>
              {settleMutation.isPending ? "Zapisywanie…" : "Oznacz jako rozliczone"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
